import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Avatar,
  Typography,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { User } from "../../contexts/user-context";
import { useUserContext } from "../../hooks/useUserContext";
import { signerManager } from "../../singletons/Signer/SignerManager";
import { useNotification } from "../../contexts/notification-context";
import { uploadToBlossom, getBlossomServer } from "../../services/blossomService";
import { signEvent } from "../../nostr";
import {
  fetchPaytoEvent,
  getPaytoTargets,
  invalidatePaytoCache,
  isValidMoneroAddressStrict,
  PAYTO_EVENT_KIND,
  buildPaytoTags,
  type PaytoTarget,
} from "../../utils/payto";
import { dataLayer } from "@formstr/local-relay";

interface ProfileEditModalProps {
  open: boolean;
  onClose: () => void;
  userProfile: User | null;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  open,
  onClose,
  userProfile,
}) => {
  const { user } = useUserContext();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [about, setAbout] = useState("");
  const [picture, setPicture] = useState("");
  const [banner, setBanner] = useState("");
  const [website, setWebsite] = useState("");
  const [nip05, setNip05] = useState("");
  const [lud16, setLud16] = useState("");
  const [moneroAddress, setMoneroAddress] = useState("");
  const [loadingMonero, setLoadingMonero] = useState(false);
  // Original monero target (if any) so we can merge with the existing 10133.
  const [existingTargets, setExistingTargets] = useState<PaytoTarget[]>([]);

  useEffect(() => {
    if (open && userProfile) {
      // The parsed kind-0 content historically lacked a pubkey; fall back to
      // the signed-in user so the payto fetch is keyed on a real pubkey.
      const profilePubkey = userProfile.pubkey || user?.pubkey || "";
      setName(userProfile.name || "");
      setDisplayName(userProfile.display_name || "");
      setAbout(userProfile.about || "");
      setPicture(userProfile.picture || "");
      setBanner(userProfile.banner || "");
      setWebsite(userProfile.website || "");
      setNip05(userProfile.nip05 || "");
      setLud16(userProfile.lud16 || "");
      // Load any existing payto targets (NIP-A3) so the Monero field is
      // pre-filled and other target types are preserved on save.
      setMoneroAddress("");
      setExistingTargets([]);
      if (!profilePubkey) {
        setLoadingMonero(false);
        return;
      }
      setLoadingMonero(true);
      fetchPaytoEvent(profilePubkey, { forceRefetch: true })
        .then((event) => {
          const targets = event ? getPaytoTargets(event) : [];
          setExistingTargets(targets);
          const monero = targets.find((t) => t.type === "monero");
          setMoneroAddress(monero ? monero.address : "");
        })
        .catch(() => {})
        .finally(() => setLoadingMonero(false));
    }
  }, [open, userProfile, user?.pubkey]);

  const [uploadingField, setUploadingField] = useState<"picture" | "banner" | null>(null);

  const handleImageUpload = async (file: File, field: "picture" | "banner") => {
    // Enforce a 5MB file size limit
    if (file.size > 5 * 1024 * 1024) {
      showNotification("File is too large! Please select an image under 5MB.", "error");
      return;
    }

    setUploadingField(field);
    try {
      const url = await uploadToBlossom(
        file,
        getBlossomServer(),
        (template) => signEvent(template, user?.privateKey)
      );
      if (field === "picture") {
        setPicture(url);
      } else {
        setBanner(url);
      }
      showNotification("Image uploaded successfully!", "success");
    } catch (err) {
      showNotification(
        "Upload failed: " + (err instanceof Error ? err.message : String(err)),
        "error"
      );
    } finally {
      setUploadingField(null);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    const cleanNip05 = nip05.trim();
    const cleanLud16 = lud16.trim();

    if (cleanNip05 && !cleanNip05.includes("@")) {
      showNotification("NIP-05 identifier must contain an '@' symbol (e.g. name@domain.com).", "error");
      return;
    }

    if (cleanLud16 && !cleanLud16.includes("@")) {
      showNotification("Lightning Address (LUD-16) must contain an '@' symbol (e.g. name@wallet.com).", "error");
      return;
    }

    const cleanMonero = moneroAddress.trim();
    if (cleanMonero && !isValidMoneroAddressStrict(cleanMonero)) {
      showNotification("Monero address looks invalid. It should be a 90-130 character primary or integrated address.", "error");
      return;
    }

    setLoading(true);
    try {
      // Publish NIP-A3 payto targets (kind 10133) when the Monero field changed.
      // Other existing target types are preserved; an empty field removes monero.
      const prevMonero = existingTargets.find((t) => t.type === "monero");
      const moneroChanged =
        (prevMonero?.address || "") !== cleanMonero;
      if (moneroChanged) {
        const targets = existingTargets.filter((t) => t.type !== "monero");
        if (cleanMonero) {
          targets.push({ type: "monero", address: cleanMonero });
        }
        if (targets.length > 0) {
          const template = {
            kind: PAYTO_EVENT_KIND,
            created_at: Math.floor(Date.now() / 1000),
            tags: buildPaytoTags(targets),
            content: "",
          };
          const signed = await signEvent(template, user.privateKey);
          await dataLayer.publishEvent(signed);
          invalidatePaytoCache(user.pubkey, signed);
        } else if (prevMonero) {
          // No targets remain — publish an empty 10133 to clear monero off relays.
          const template = {
            kind: PAYTO_EVENT_KIND,
            created_at: Math.floor(Date.now() / 1000),
            tags: [],
            content: "",
          };
          const signed = await signEvent(template, user.privateKey);
          await dataLayer.publishEvent(signed);
          invalidatePaytoCache(user.pubkey, signed);
        }
      }

      await signerManager.publishKind0({
        pubkey: user.pubkey,
        name: name.trim(),
        display_name: displayName.trim(),
        about: about.trim(),
        picture: picture.trim(),
        banner: banner.trim(),
        website: website.trim(),
        nip05: cleanNip05,
        lud16: cleanLud16,
      });
      showNotification("Profile updated successfully!");
      
      // Delay slightly so the relays can propagate, then notify the SignerManager to refresh cache
      setTimeout(() => {
        signerManager.afterLogin(user.pubkey);
      }, 1000);
      
      onClose();
    } catch (err) {
      console.error("Failed to update profile:", err);
      showNotification(
        "Failed to update profile: " + (err instanceof Error ? err.message : String(err)),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Name / Username"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. alice"
          />
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. Alice Nakamoto"
          />
          <TextField
            label="About"
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
          />
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" color="text.secondary">Profile Picture</Typography>
            <Box display="flex" flexDirection="row" gap={2} alignItems="center">
              <Avatar src={picture} sx={{ width: 64, height: 64 }} />
              <Button
                component="label"
                variant="outlined"
                startIcon={uploadingField === "picture" ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                disabled={uploadingField === "picture"}
              >
                Upload Picture
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleImageUpload(e.target.files[0], "picture");
                    e.target.value = "";
                  }}
                />
              </Button>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" color="text.secondary">Banner Image</Typography>
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: 1,
                background: banner ? `url(${banner}) center/cover no-repeat` : "#e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed #ccc",
              }}
            >
              <Button
                component="label"
                variant="contained"
                startIcon={uploadingField === "banner" ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                disabled={uploadingField === "banner"}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.6)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                }}
              >
                Upload Banner
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleImageUpload(e.target.files[0], "banner");
                    e.target.value = "";
                  }}
                />
              </Button>
            </Box>
          </Box>
          <TextField
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://..."
          />
          <TextField
            label="NIP-05 Identifier"
            value={nip05}
            onChange={(e) => setNip05(e.target.value)}
            fullWidth
            size="small"
            placeholder="name@domain.com"
          />
          <TextField
            label="Lightning Address (LUD-16)"
            value={lud16}
            onChange={(e) => setLud16(e.target.value)}
            fullWidth
            size="small"
            placeholder="name@wallet.com"
          />
          <TextField
            label="Monero Address (NIP-A3)"
            value={moneroAddress}
            onChange={(e) => setMoneroAddress(e.target.value)}
            fullWidth
            size="small"
            placeholder="4A... (receive Monero zaps)"
            helperText="Published as a payto target (kind 10133) so others can zap you XMR."
            InputProps={{
              endAdornment: loadingMonero ? (
                <CircularProgress size={18} />
              ) : undefined,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={loading} variant="contained">
          {loading ? <CircularProgress size={24} /> : "Save Profile"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
